import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Globe, MapPin, Plus, LayoutDashboard, BellRing, ChevronLeft, ChevronRight, LayoutGrid, CalendarRange, Printer, BellPlus } from "lucide-react";
import { parse, startOfWeek, addDays, format, isSameDay, isToday } from "date-fns";
import { toast } from "sonner";
import api from "../api";
import Modal from "../components/Modal";
import PatientPicker from "../components/PatientPicker";
import TreatmentSelect from "../components/TreatmentSelect";
import { inputCls, labelCls, todayIso, toApiDate, toApiTime, fromApiDate, fromApiTime } from "../utils/datetime";

const STATUS_STYLE = {
  Confirmed: "bg-teal-soft text-teal-brand",
  "In Progress": "bg-blue-50 text-blue-600",
  Upcoming: "bg-gold-soft/60 text-pine-deep",
  Completed: "bg-cream text-pine/60",
  Cancelled: "bg-red-50 text-red-500",
};
const STATUS_DOT = {
  Confirmed: "border-teal-brand",
  "In Progress": "border-blue-500",
  Upcoming: "border-gold",
  Completed: "border-slate-300",
  Cancelled: "border-red-400",
};
const STATUSES = ["Upcoming", "Confirmed", "In Progress", "Completed", "Cancelled"];

const parseApptDate = (s) => {
  if (!s) return null;
  const clean = String(s).replace(/(\d+)(st|nd|rd|th)/g, "$1").trim();
  for (const f of ["d MMM yyyy", "MMMM d, yyyy", "MMM d, yyyy", "d MMMM yyyy"]) {
    const d = parse(clean, f, new Date());
    if (!isNaN(d)) return d;
  }
  return null;
};

const timeToMinutes = (t) => {
  const m = /(\d+):(\d+)\s*(AM|PM)/i.exec(t || "");
  if (!m) return 9999;
  let h = Number(m[1]) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return h * 60 + Number(m[2]);
};

const EMPTY = { patient_id: "", name: "", phone: "", date: todayIso(), time: "", service: "", doctor: "Dr. Ankita Pandey", notes: "" };
const EMPTY_FOLLOWUP = { due_date: "", time: "", treatment: "", notes: "" };

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [view, setView] = useState("calendar");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [day, setDay] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [pickedPatient, setPickedPatient] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ date: "", time: "", doctor: "", status: "", notes: "" });
  const [followup, setFollowup] = useState(EMPTY_FOLLOWUP);
  const [printing, setPrinting] = useState(false);
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");

  const openEdit = (a) => {
    setEditing(a);
    setEditForm({ date: fromApiDate(a.date), time: fromApiTime(a.time), doctor: a.doctor || "Dr. Ankita Pandey", status: a.status || "Upcoming", notes: a.notes || a.message || "", service: a.service || "" });
    setFollowup({ ...EMPTY_FOLLOWUP, treatment: a.service || "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/connect/appointments/${editing.id}`, {
        ...editForm,
        date: toApiDate(editForm.date),
        time: toApiTime(editForm.time),
      });
      toast.success(`Appointment updated for ${editing.name}`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update the appointment");
    }
  };

  const createFollowup = async () => {
    if (!followup.due_date) { toast.error("Pick the next follow-up date"); return; }
    try {
      await api.post("/connect/followups", {
        patient: editing.name,
        patient_id: editing.patient_id || "",
        treatment: followup.treatment || editing.service || "",
        due_date: toApiDate(followup.due_date),
        time: toApiTime(followup.time),
        notes: followup.notes,
      });
      toast.success(`Follow-up created for ${editing.name}`);
      setFollowup({ ...EMPTY_FOLLOWUP, treatment: editing.service || "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the follow-up");
    }
  };

  const removeAppt = async () => {
    try {
      await api.delete(`/connect/appointments/${editing.id}`);
      toast.success(`Appointment for ${editing.name} deleted`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete the appointment");
    }
  };

  // Touch-friendly: tapping a calendar day (or its + button) opens the form pre-filled with that date.
  const bookForDate = (d) => {
    setForm({ ...EMPTY, date: format(d, "yyyy-MM-dd") });
    setPickedPatient(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const load = () => api.get("/connect/appointments").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/connect/appointments", {
        ...form,
        patient_id: pickedPatient?.patient_id || "",
        date: toApiDate(form.date),
        time: toApiTime(form.time),
      });
      toast.success("Appointment booked");
      setForm(EMPTY);
      setPickedPatient(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not book the appointment");
    }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/connect/appointments/${id}`, { status });
    load();
  };

  const runReminders = async () => {
    try {
      const { data } = await api.post("/connect/reminders/run");
      if (data.skipped) toast.info("Reminders are ready but inactive — Twilio WhatsApp keys are not configured yet.");
      else toast.success(`Sent ${data.sent} WhatsApp reminder(s) for tomorrow's appointments.`);
    } catch {
      toast.error("Could not run reminders");
    }
  };

  const filtered = items.filter((a) => filter === "All" || (a.source || "Walk-in") === filter || (filter === "Walk-in" && !a.source));

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const byDay = useMemo(() => {
    const map = new Map();
    filtered.forEach((a) => {
      const d = parseApptDate(a.date);
      if (!d) return;
      const key = format(d, "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    map.forEach((list) => list.sort((x, y) => timeToMinutes(x.time) - timeToMinutes(y.time)));
    return map;
  }, [filtered]);

  return (
    <div data-testid="connect-appointments" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Appointments</h2>
          <p className="text-sm text-slate-500">Website bookings, walk-ins and dashboard bookings in one schedule.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["All", "Website", "Walk-in", "Dashboard"].map((f) => (
            <button key={f} data-testid={`appt-filter-${f.toLowerCase().replace(" ", "-")}`} onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-2 text-xs font-bold border transition-all ${filter === f ? "bg-pine text-ivory border-pine" : "bg-white text-pine border-pine/15"}`}>
              {f}
            </button>
          ))}
          <span className="w-px bg-pine/10 mx-1 hidden sm:block" />
          <div className="flex rounded-full border border-pine/15 overflow-hidden">
            <button data-testid="appt-view-day" onClick={() => setView("day")}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold transition-all ${view === "day" ? "bg-pine text-ivory" : "bg-white text-pine"}`}>
              <CalendarDays className="h-3.5 w-3.5" /> Day
            </button>
            <button data-testid="appt-view-calendar" onClick={() => setView("calendar")}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold transition-all ${view === "calendar" ? "bg-pine text-ivory" : "bg-white text-pine"}`}>
              <CalendarRange className="h-3.5 w-3.5" /> Week
            </button>
            <button data-testid="appt-view-cards" onClick={() => setView("cards")}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold transition-all ${view === "cards" ? "bg-pine text-ivory" : "bg-white text-pine"}`}>
              <LayoutGrid className="h-3.5 w-3.5" /> Cards
            </button>
          </div>
          <button data-testid="appt-reminders" onClick={runReminders}
            className="inline-flex items-center gap-2 rounded-full border border-pine/20 px-4 py-2 text-xs font-bold text-pine hover:border-gold transition-all">
            <BellRing className="h-4 w-4" /> Send Tomorrow's Reminders
          </button>
          <button data-testid="appt-add-toggle" onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-4 py-2 text-xs font-bold text-white hover:bg-pine transition-all">
            <Plus className="h-4 w-4" /> New Appointment
          </button>
        </div>
      </div>

      {showForm && (
        <form data-testid="appt-add-form" onSubmit={add} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="sm:col-span-2 lg:col-span-2">
            <label className={labelCls}>Existing Patient (optional — auto-fills name & phone)</label>
            <div className="mt-1">
              <PatientPicker testid="appt-patient-picker" value={pickedPatient}
                onSelect={(p) => { setPickedPatient(p); if (p) setForm({ ...form, name: p.name, phone: p.phone }); }} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Treatment *</label>
            <div className="mt-1"><TreatmentSelect testid="appt-service" value={form.service} onChange={(v) => setForm({ ...form, service: v })} /></div>
          </div>
          {!pickedPatient && (
            <>
              <div>
                <label className={labelCls}>Patient Name *</label>
                <input required={!pickedPatient} minLength={2} data-testid="appt-name" placeholder="Patient name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input required={!pickedPatient} type="tel" inputMode="tel" data-testid="appt-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
            </>
          )}
          <div>
            <label className={labelCls}>Date *</label>
            <input required type="date" data-testid="appt-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div>
            <label className={labelCls}>Time *</label>
            <input required type="time" data-testid="appt-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div>
            <label className={labelCls}>Doctor</label>
            <input data-testid="appt-doctor" value={form.doctor} onChange={(e) => setForm({ ...form, doctor: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div className="sm:col-span-2 lg:col-span-2">
            <label className={labelCls}>Notes</label>
            <textarea data-testid="appt-notes" rows={2} placeholder="Anything the doctor should know…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 w-full rounded-xl border border-pine/15 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white" />
          </div>
          <div className="flex items-end">
            <button data-testid="appt-submit" className="h-11 w-full rounded-full bg-pine text-ivory text-xs font-bold hover:bg-pine-deep transition-colors">Book Appointment</button>
          </div>
        </form>
      )}

      {view === "day" ? (
        <div data-testid="appt-day-view" className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-pine/8">
            <button data-testid="day-prev" onClick={() => setDay(addDays(day, -1))} className="rounded-full border border-pine/15 p-2 text-pine hover:bg-cream transition-colors" aria-label="Previous day">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-center">
              <p className="font-display text-lg font-semibold text-pine-deep">{format(day, "EEEE, d MMMM yyyy")}</p>
              <button data-testid="day-today" onClick={() => setDay(new Date())} className="text-[11px] font-bold text-teal-brand hover:text-pine">
                Jump to today
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="day-print" onClick={() => setPrinting(true)} className="inline-flex items-center gap-1.5 rounded-full border border-pine/20 px-3.5 py-2 text-xs font-bold text-pine hover:border-gold transition-all">
                <Printer className="h-3.5 w-3.5" /> Print Day Sheet
              </button>
              <button data-testid="day-next" onClick={() => setDay(addDays(day, 1))} className="rounded-full border border-pine/15 p-2 text-pine hover:bg-cream transition-colors" aria-label="Next day">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="divide-y divide-pine/6">
            {Array.from({ length: 12 }, (_, i) => i + 9).map((h) => {
              const label = `${String(h % 12 === 0 ? 12 : h % 12).padStart(2, "0")}:00 ${h < 12 ? "AM" : "PM"}`;
              const hourAppts = (byDay.get(format(day, "yyyy-MM-dd")) || []).filter((a) => Math.floor(timeToMinutes(a.time) / 60) === h);
              return (
                <div key={h} data-testid={`day-row-${h}`} className="flex min-h-[64px]">
                  <div className="w-24 shrink-0 px-4 py-3 text-xs font-bold text-slate-400 border-r border-pine/6 bg-cream/30">{label}</div>
                  <div className="flex-1 p-2 flex flex-wrap gap-2 items-start">
                    {hourAppts.map((a) => (
                      <button key={a.id} data-testid={`day-slot-${a.id.slice(0, 8)}`} onClick={() => openEdit(a)}
                        className={`rounded-lg border-l-4 bg-white shadow-soft px-3.5 py-2 text-left transition-all hover:shadow-lift hover:-translate-y-0.5 cursor-pointer ${STATUS_DOT[a.status] || STATUS_DOT.Upcoming}`}>
                        <p className="text-[10px] font-bold text-teal-brand">{a.time}</p>
                        <p className="text-xs font-bold text-pine-deep leading-tight">{a.name} <span className="font-normal text-slate-500">· {a.service}</span></p>
                      </button>
                    ))}
                    {hourAppts.length === 0 && <span className="self-center text-[10px] text-slate-200 font-bold select-none">—</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="px-5 py-3 text-[11px] text-slate-400 border-t border-pine/6">
            {(byDay.get(format(day, "yyyy-MM-dd")) || []).length} appointment(s) this day · 9 AM – 8 PM · Click a slot to edit
          </p>
        </div>
      ) : view === "calendar" ? (
        <div data-testid="appt-calendar" className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-pine/8">
            <button data-testid="cal-prev" onClick={() => setWeekStart(addDays(weekStart, -7))} className="rounded-full border border-pine/15 p-2 text-pine hover:bg-cream transition-colors" aria-label="Previous week">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-center">
              <p className="font-display text-lg font-semibold text-pine-deep">
                {format(days[0], "d MMM")} – {format(days[6], "d MMM yyyy")}
              </p>
              <button data-testid="cal-today" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="text-[11px] font-bold text-teal-brand hover:text-pine">
                Jump to this week
              </button>
            </div>
            <button data-testid="cal-next" onClick={() => setWeekStart(addDays(weekStart, 7))} className="rounded-full border border-pine/15 p-2 text-pine hover:bg-cream transition-colors" aria-label="Next week">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="overflow-x-auto" data-lenis-prevent>
            <div className="grid grid-cols-7 min-w-[980px]">
              {days.map((d) => {
                const list = byDay.get(format(d, "yyyy-MM-dd")) || [];
                const today = isToday(d);
                return (
                  <div key={d.toISOString()} data-testid={`cal-day-${format(d, "yyyy-MM-dd")}`} className={`min-h-[320px] border-r border-pine/6 last:border-0 ${today ? "bg-gold-soft/20" : ""}`}>
                    <div className={`px-3 py-3 text-center border-b border-pine/6 ${today ? "bg-gold-soft/50" : "bg-cream/40"}`}>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{format(d, "EEE")}</p>
                      <p className={`font-display text-xl font-semibold ${today ? "text-pine-deep" : "text-slate-600"}`}>{format(d, "d")}</p>
                    </div>
                    <div className="p-2 space-y-2">
                      {list.map((a) => (
                        <button key={a.id} data-testid={`cal-slot-${a.id.slice(0, 8)}`} onClick={() => openEdit(a)}
                          className={`w-full text-left rounded-lg border-l-4 bg-white shadow-soft p-2.5 transition-all hover:shadow-lift hover:-translate-y-0.5 cursor-pointer ${STATUS_DOT[a.status] || STATUS_DOT.Upcoming}`}>
                          <p className="text-[10px] font-bold text-teal-brand">{a.time}</p>
                          <p className="text-xs font-bold text-pine-deep leading-tight mt-0.5">{a.name}</p>
                          <p className="text-[10px] text-slate-500 leading-tight">{a.service}</p>
                        </button>
                      ))}
                      <button
                        data-testid={`cal-book-${format(d, "yyyy-MM-dd")}`}
                        onClick={() => bookForDate(d)}
                        className="w-full rounded-lg border border-dashed border-pine/20 py-2 text-[10px] font-bold text-slate-400 hover:text-teal-brand hover:border-teal-brand/40 transition-colors"
                        aria-label={`Book appointment on ${format(d, "d MMM")}`}
                      >
                        + Book
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((a) => (
            <div key={a.id} data-testid={`appt-card-${a.id.slice(0, 8)}`} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 hover:-translate-y-0.5 hover:shadow-lift transition-all">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-bold text-pine-deep">{a.name}</p>
                  <p className="text-xs font-semibold text-teal-brand mt-0.5">{a.service}</p>
                </div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ${a.source === "Website" ? "bg-gold-soft/60 text-pine-deep" : a.source === "Dashboard" ? "bg-teal-soft text-teal-brand" : "bg-cream text-pine/60"}`}>
                  {a.source === "Website" ? <Globe className="h-3 w-3" /> : a.source === "Dashboard" ? <LayoutDashboard className="h-3 w-3" /> : <MapPin className="h-3 w-3" />} {a.source || "Walk-in"}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <CalendarDays className="h-3.5 w-3.5 text-gold" /> {a.date} · {a.time}
              </div>
              <p className="mt-1 text-xs text-slate-500">{a.phone} {a.doctor ? `· ${a.doctor}` : ""}</p>
              {a.message && <p className="mt-2 text-xs text-slate-400 italic line-clamp-2">"{a.message}"</p>}
              <div className="mt-4">
                <select
                  data-testid={`appt-status-${a.id.slice(0, 8)}`}
                  value={a.status || "Upcoming"}
                  onChange={(e) => setStatus(a.id, e.target.value)}
                  className={`w-full rounded-full px-3.5 py-2 text-xs font-bold border-0 cursor-pointer ${STATUS_STYLE[a.status] || STATUS_STYLE.Upcoming}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="md:col-span-2 xl:col-span-3 rounded-2xl bg-white border border-pine/8 p-10 text-center text-slate-400 text-sm">
              No appointments found.
            </div>
          )}
        </div>
      )}
      {printing && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto p-6 md:p-10 print-sheet-wrap">
          <div className="max-w-3xl mx-auto print-sheet">
            <div className="flex items-center justify-between border-b-2 border-pine pb-4">
              <div className="flex items-center gap-3">
                <img src="/dyntra-logo.jpg" alt="" className="h-12 w-12 rounded-full object-cover" />
                <div>
                  <p className="font-display text-xl font-semibold text-pine-deep">Dyntra Clinics — Sector 75, Noida</p>
                  <p className="text-xs text-slate-500">Daily Appointment Sheet · {format(day, "EEEE, d MMMM yyyy")}</p>
                </div>
              </div>
              <div className="text-right text-xs text-slate-500">
                <p>+91 9801910117</p>
                <p>info@dyntra.store</p>
              </div>
            </div>
            <table className="w-full text-sm mt-5">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-pine/20">
                  <th className="py-2.5 pr-4 font-bold">Time</th>
                  <th className="py-2.5 pr-4 font-bold">Patient</th>
                  <th className="py-2.5 pr-4 font-bold">Service</th>
                  <th className="py-2.5 pr-4 font-bold">Doctor</th>
                  <th className="py-2.5 pr-4 font-bold">Status</th>
                  <th className="py-2.5 font-bold">Phone</th>
                </tr>
              </thead>
              <tbody>
                {(byDay.get(format(day, "yyyy-MM-dd")) || []).map((a) => (
                  <tr key={a.id} className="border-b border-pine/8">
                    <td className="py-2.5 pr-4 font-bold text-pine-deep whitespace-nowrap">{a.time}</td>
                    <td className="py-2.5 pr-4">{a.name}</td>
                    <td className="py-2.5 pr-4">{a.service}</td>
                    <td className="py-2.5 pr-4">{a.doctor || "Dr. Ankita Pandey"}</td>
                    <td className="py-2.5 pr-4">{a.status || "Upcoming"}</td>
                    <td className="py-2.5">{a.phone}</td>
                  </tr>
                ))}
                {(byDay.get(format(day, "yyyy-MM-dd")) || []).length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-slate-400">No appointments scheduled for this day.</td></tr>
                )}
              </tbody>
            </table>
            <p className="mt-6 text-[11px] text-slate-400 border-t border-pine/10 pt-3">
              Generated from Dyntra Connect · {(byDay.get(format(day, "yyyy-MM-dd")) || []).length} appointment(s) · Printed {format(new Date(), "d MMM yyyy, hh:mm a")}
            </p>
          </div>
          <div className="max-w-3xl mx-auto mt-6 flex gap-3 no-print">
            <button data-testid="day-print-confirm" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full bg-pine px-6 py-3 text-sm font-bold text-ivory hover:bg-pine-deep transition-colors">
              <Printer className="h-4 w-4" /> Print / Save as PDF
            </button>
            <button data-testid="day-print-close" onClick={() => setPrinting(false)} className="rounded-full border border-pine/20 px-6 py-3 text-sm font-bold text-pine hover:bg-cream transition-colors">
              Back to Calendar
            </button>
          </div>
        </div>
      )}
      {editing && (
        <Modal testid="appt-edit-modal" onClose={() => setEditing(null)}
          title={editing.name}
          subtitle={`${editing.patient_id ? editing.patient_id + " · " : ""}${editing.phone} · ${editing.service} · ${editing.date} at ${editing.time}`}>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Date</label>
                <input required type="date" data-testid="appt-edit-date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
              <div>
                <label className={labelCls}>Time</label>
                <input required type="time" data-testid="appt-edit-time" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Treatment</label>
              <div className="mt-1"><TreatmentSelect testid="appt-edit-treatment" value={editForm.service} onChange={(v) => setEditForm({ ...editForm, service: v })} /></div>
            </div>
            <div>
              <label className={labelCls}>Doctor</label>
              <input data-testid="appt-edit-doctor" value={editForm.doctor} onChange={(e) => setEditForm({ ...editForm, doctor: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select data-testid="appt-edit-status" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className={`mt-1 ${inputCls}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Notes</label>
              <textarea data-testid="appt-edit-notes" rows={2} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="mt-1 w-full rounded-xl border border-pine/15 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white" />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="submit" data-testid="appt-edit-save" className="flex-1 rounded-full bg-pine px-5 py-3 text-sm font-bold text-ivory hover:bg-pine-deep transition-colors">Save Changes</button>
              {["super_admin", "owner", "admin", "clinic_manager"].includes(me.level || "") && (
                <button type="button" data-testid="appt-edit-delete" onClick={removeAppt} className="rounded-full border border-red-200 px-5 py-3 text-sm font-bold text-red-500 hover:bg-red-50 transition-colors">Delete</button>
              )}
              <button type="button" data-testid="appt-edit-cancel" onClick={() => setEditing(null)} className="rounded-full border border-pine/20 px-5 py-3 text-sm font-bold text-pine hover:bg-cream transition-colors">Cancel</button>
            </div>
          </form>
          <div className="border-t border-pine/8 pt-4 space-y-3">
            <p className="text-xs font-bold text-pine/60 flex items-center gap-1.5"><BellPlus className="h-4 w-4 text-teal-brand" /> Create Next Follow-up</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Follow-up Date</label>
                <input type="date" data-testid="followup-date" value={followup.due_date} onChange={(e) => setFollowup({ ...followup, due_date: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
              <div>
                <label className={labelCls}>Time</label>
                <input type="time" data-testid="followup-time" value={followup.time} onChange={(e) => setFollowup({ ...followup, time: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Treatment</label>
              <div className="mt-1"><TreatmentSelect testid="followup-treatment" value={followup.treatment} onChange={(v) => setFollowup({ ...followup, treatment: v })} /></div>
            </div>
            <div>
              <label className={labelCls}>Notes</label>
              <textarea data-testid="followup-notes" rows={2} value={followup.notes} onChange={(e) => setFollowup({ ...followup, notes: e.target.value })}
                className="mt-1 w-full rounded-xl border border-pine/15 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white" />
            </div>
            <button type="button" data-testid="followup-create" onClick={createFollowup}
              className="w-full rounded-full bg-teal-brand px-5 py-3 text-sm font-bold text-white hover:bg-pine transition-colors">
              Save Follow-up
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
